/**
 * Cookie Consent Manager
 * GDPR/CCPA 컴플라이언스를 위한 쿠키 동의 관리
 *
 * 역할 분담 (2026-07 개편):
 * - 동의의 진실원본은 Shopify Customer Privacy API 다. localStorage 는 UI 복원용 캐시일 뿐이며,
 *   Shopify 에 기록된 값이 있으면 그쪽을 정본으로 채택한다.
 * - 배너 UI 는 기본적으로 Shopify 자체 쿠키 배너가 담당한다. 이 커스텀 배너는 Shopify 배너가
 *   뜨지 않는 상황의 **폴백**이다 (섹션 설정 "Banner role" = fallback). Shopify 배너를 어드민에서
 *   끈 경우에만 primary 로 바꿔 쓴다.
 * - 지역 판정은 추측하지 않는다. privacy.getRegion() / saleOfDataRegion() / shouldShowBanner() 만 믿고,
 *   API 를 못 얻으면 안전한 쪽(표시)으로 기운다.
 */

if (!window.CookieConsentManager) {

  // 쿠키 동의 상태 상수
  const CONSENT_STATUS = {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    CUSTOM: 'custom'
  };

  // 쿠키 카테고리
  const COOKIE_CATEGORIES = {
    ESSENTIAL: 'essential',     // 필수 쿠키 (항상 허용)
    FUNCTIONAL: 'functional',   // 기능 쿠키 (최근 본 상품 등) → Shopify 의 preferences
    ANALYTICS: 'analytics',     // 분석 쿠키
    MARKETING: 'marketing'      // 마케팅 쿠키 (+ CCPA sale_of_data)
  };

  // 지역 설정 모드
  const REGION_MODE = {
    AUTO: 'auto',               // Shopify 판정에 위임 (권장)
    EU_ONLY: 'eu_only',         // EU/EEA 지역만
    CCPA_ONLY: 'ccpa_only',     // 판매 옵트아웃 대상 지역만
    ALL: 'all',                 // 모든 지역
    DISABLED: 'disabled'        // 비활성화
  };

  // 배너 역할
  const BANNER_ROLE = {
    FALLBACK: 'fallback',       // Shopify 배너가 뜨면 양보한다
    PRIMARY: 'primary'          // 이 배너가 유일한 동의 UI 다
  };

  // EU/EEA + 동등 규제 국가. privacy.getRegion() 이 주는 ISO 3166-2 의 앞 2자리와 대조한다.
  const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU
    'IS', 'LI', 'NO', // EEA
    'GB', 'CH' // UK & Switzerland
  ];

  /**
   * Cookie Consent Manager 클래스
   * 전역으로 사용 가능한 동의 관리 시스템
   */
  class CookieConsentManager {
    constructor() {
      this.cookieName = 'cookie_consent';
      this.cookieDuration = 365; // days
      this.consent = this.loadConsent();
      this.listeners = [];
      this.privacy = null;          // window.Shopify.customerPrivacy (로드 성공 시)
      this.region = null;           // ISO 3166-2 ("DE", "US-CA")
      this.saleOfDataRegion = false;
      this.regionMode = REGION_MODE.AUTO;
      // 이 배너가 유일한 동의 UI 인가 (BANNER_ROLE.PRIMARY). shouldShowBanner() 만 믿으면
      // 안 되는 상황을 가르는 플래그다 — shouldShowForRegion() 주석 참조.
      this.assumePrimary = false;

      // 외부(Shopify 배너·체크아웃·앱)에서 동의가 바뀌는 것을 먼저 구독해 둔다.
      this.watchExternalConsent();

      // API 로드는 비동기다. 배너는 이 Promise 가 끝난 뒤에 표시 여부를 판단한다.
      this.ready = this.initShopifyPrivacy();
    }

    /**
     * Shopify Customer Privacy API 로드
     * @returns {Promise<boolean>} 사용 가능 여부
     */
    initShopifyPrivacy() {
      return new Promise((resolve) => {
        if (typeof Shopify === 'undefined' || typeof Shopify.loadFeatures !== 'function') {
          resolve(false);
          return;
        }

        Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error || !window.Shopify || !window.Shopify.customerPrivacy) {
            resolve(false);
            return;
          }

          this.privacy = window.Shopify.customerPrivacy;
          this.readRegion();
          this.adoptShopifyConsent();
          resolve(true);
        });
      });
    }

    /**
     * 방문자 지역 정보 읽기 (추측하지 않는다)
     */
    readRegion() {
      try {
        if (typeof this.privacy.getRegion === 'function') {
          this.region = this.privacy.getRegion() || null;
        }
      } catch (e) {
        this.region = null;
      }

      try {
        if (typeof this.privacy.saleOfDataRegion === 'function') {
          this.saleOfDataRegion = this.privacy.saleOfDataRegion() === true;
        }
      } catch (e) {
        this.saleOfDataRegion = false;
      }
    }

    /**
     * Shopify 에 이미 기록된 동의가 있으면 그것을 정본으로 채택한다.
     * (다른 기기·이전 방문·체크아웃에서 결정한 값이 로컬 캐시보다 우선한다)
     */
    adoptShopifyConsent() {
      let current = null;

      try {
        if (typeof this.privacy.currentVisitorConsent === 'function') {
          current = this.privacy.currentVisitorConsent();
        }
      } catch (e) {
        return;
      }

      if (!current) return;

      // 미결정 상태는 빈 문자열로 온다. 하나라도 결정돼 있어야 채택한다.
      const decided = ['analytics', 'marketing', 'preferences'].some((key) => this.isDecided(current[key]));
      if (!decided) return;

      this.consent.categories[COOKIE_CATEGORIES.ANALYTICS] = this.isGranted(current.analytics);
      this.consent.categories[COOKIE_CATEGORIES.MARKETING] = this.isGranted(current.marketing);
      this.consent.categories[COOKIE_CATEGORIES.FUNCTIONAL] = this.isGranted(current.preferences);
      this.consent.status = this.deriveStatus();

      this.persist();
      this.notifyListeners();
    }

    isDecided(value) {
      return value === 'yes' || value === 'no' || value === true || value === false;
    }

    isGranted(value) {
      return value === 'yes' || value === true;
    }

    /**
     * 카테고리 조합으로 상태 문자열을 되돌린다 (동의가 실제로 수집된 뒤에만 호출할 것)
     */
    deriveStatus() {
      const c = this.consent.categories;
      const optional = [c[COOKIE_CATEGORIES.FUNCTIONAL], c[COOKIE_CATEGORIES.ANALYTICS], c[COOKIE_CATEGORIES.MARKETING]];

      if (optional.every((granted) => granted === true)) return CONSENT_STATUS.ACCEPTED;
      if (optional.every((granted) => granted !== true)) return CONSENT_STATUS.REJECTED;
      return CONSENT_STATUS.CUSTOM;
    }

    /**
     * Shopify 배너·체크아웃·다른 앱에서 동의가 바뀌면 로컬 캐시를 따라가게 한다.
     * ⚠️ 여기서 setTrackingConsent 를 다시 부르면 이벤트가 재발화해 무한 루프가 된다 → persist() 만 한다.
     */
    watchExternalConsent() {
      document.addEventListener('visitorConsentCollected', (event) => {
        const detail = event.detail || {};

        this.consent.categories[COOKIE_CATEGORIES.ANALYTICS] = detail.analyticsAllowed === true;
        this.consent.categories[COOKIE_CATEGORIES.MARKETING] = detail.marketingAllowed === true;
        this.consent.categories[COOKIE_CATEGORIES.FUNCTIONAL] = detail.preferencesAllowed === true;
        this.consent.status = this.deriveStatus();

        this.persist();
        this.notifyListeners();
      });
    }

    /**
     * 저장된 동의 상태 로드
     */
    loadConsent() {
      const defaultConsent = {
        status: CONSENT_STATUS.PENDING,
        categories: {
          [COOKIE_CATEGORIES.ESSENTIAL]: true,
          [COOKIE_CATEGORIES.FUNCTIONAL]: false,
          [COOKIE_CATEGORIES.ANALYTICS]: false,
          [COOKIE_CATEGORIES.MARKETING]: false
        },
        timestamp: null,
        version: '2.0'
      };

      try {
        const stored = localStorage.getItem(this.cookieName);
        if (stored) {
          const parsed = JSON.parse(stored);
          return {
            ...defaultConsent,
            ...parsed,
            categories: { ...defaultConsent.categories, ...(parsed.categories || {}) }
          };
        }
      } catch (e) {
        // 캐시가 깨졌으면 기본값으로 시작한다
      }

      return defaultConsent;
    }

    /**
     * 로컬 캐시에만 기록 (Shopify 로 되밀지 않는다)
     */
    persist() {
      try {
        this.consent.timestamp = Date.now();
        localStorage.setItem(this.cookieName, JSON.stringify(this.consent));

        // 서버 사이드에서도 확인할 수 있도록 쿠키로도 남긴다
        if (window.WokieeCookies) {
          WokieeCookies.set(this.cookieName, this.consent.status, this.cookieDuration, 'days');
        }
      } catch (e) {
        // localStorage 차단 환경 — 세션 내 메모리 상태만으로 동작한다
      }
    }

    /**
     * 로컬 캐시 저장 + Shopify 반영 + 리스너 통지
     */
    saveConsent() {
      this.persist();
      this.pushToShopify();
      this.notifyListeners();
    }

    /**
     * Shopify Customer Privacy API 에 동의 기록
     * sale_of_data 는 CCPA "개인정보 판매/공유" 옵트아웃 항목이라 마케팅 동의와 함께 간다.
     */
    pushToShopify() {
      if (!this.privacy || typeof this.privacy.setTrackingConsent !== 'function') return;

      const c = this.consent.categories;

      try {
        this.privacy.setTrackingConsent({
          analytics: c[COOKIE_CATEGORIES.ANALYTICS] === true,
          marketing: c[COOKIE_CATEGORIES.MARKETING] === true,
          preferences: c[COOKIE_CATEGORIES.FUNCTIONAL] === true,
          sale_of_data: c[COOKIE_CATEGORIES.MARKETING] === true
        }, () => {});
      } catch (e) {
        // API 가 거부해도 로컬 상태는 유지한다
      }
    }

    /**
     * 모든 쿠키 수락
     */
    acceptAll() {
      this.consent.status = CONSENT_STATUS.ACCEPTED;
      this.consent.categories = {
        [COOKIE_CATEGORIES.ESSENTIAL]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: true,
        [COOKIE_CATEGORIES.ANALYTICS]: true,
        [COOKIE_CATEGORIES.MARKETING]: true
      };
      this.saveConsent();
    }

    /**
     * 모든 쿠키 거부 (필수 제외)
     */
    rejectAll() {
      this.consent.status = CONSENT_STATUS.REJECTED;
      this.consent.categories = {
        [COOKIE_CATEGORIES.ESSENTIAL]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: false,
        [COOKIE_CATEGORIES.ANALYTICS]: false,
        [COOKIE_CATEGORIES.MARKETING]: false
      };
      this.saveConsent();
      this.clearNonEssentialCookies();
    }

    /**
     * 사용자 정의 동의 저장
     */
    saveCustom(categories) {
      this.consent.categories = {
        ...this.consent.categories,
        ...categories,
        [COOKIE_CATEGORIES.ESSENTIAL]: true // 항상 true
      };
      this.consent.status = this.deriveStatus();
      this.saveConsent();
      this.clearRejectedCookies();
    }

    /**
     * 비필수 쿠키 삭제
     */
    clearNonEssentialCookies() {
      if (window.WokieeCookies) {
        WokieeCookies.remove('recently_viewed');
      }

      ['_ga', '_gid', '_fbp', '_fbc'].forEach((name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
    }

    /**
     * 거부된 카테고리의 쿠키 삭제
     */
    clearRejectedCookies() {
      if (!this.consent.categories[COOKIE_CATEGORIES.FUNCTIONAL] && window.WokieeCookies) {
        WokieeCookies.remove('recently_viewed');
      }

      if (!this.consent.categories[COOKIE_CATEGORIES.ANALYTICS]) {
        ['_ga', '_gid'].forEach((name) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
      }

      if (!this.consent.categories[COOKIE_CATEGORIES.MARKETING]) {
        ['_fbp', '_fbc'].forEach((name) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
      }
    }

    /**
     * 특정 카테고리가 허용되었는지 확인
     */
    isAllowed(category) {
      if (category === COOKIE_CATEGORIES.ESSENTIAL) return true;
      if (this.consent.status === CONSENT_STATUS.PENDING) return false;
      return this.consent.categories[category] === true;
    }

    /**
     * 동의가 필요한지 확인 (지역 기반)
     */
    needsConsent() {
      if (this.consent.status !== CONSENT_STATUS.PENDING) return false;
      return this.shouldShowForRegion();
    }

    /**
     * 지역 모드 설정
     */
    setRegionMode(mode) {
      if (Object.values(REGION_MODE).includes(mode)) {
        this.regionMode = mode;
      }
    }

    /**
     * 현재 지역에서 배너를 표시해야 하는지 확인
     */
    shouldShowForRegion() {
      switch (this.regionMode) {
        case REGION_MODE.DISABLED:
          return false;

        case REGION_MODE.ALL:
          return true;

        case REGION_MODE.EU_ONLY:
          return this.isEURegion();

        case REGION_MODE.CCPA_ONLY:
          return this.isCCPARegion();

        case REGION_MODE.AUTO:
        default:
          if (this.shopifyShouldShowBanner()) return true;

          // ⚠️ primary 역할일 때의 안전망.
          // 어드민에서 Shopify 자체 쿠키 배너를 끄면 shouldShowBanner() 가 규제 지역
          // 방문자에게도 false 로 떨어질 수 있다. fallback 이면 "안 띄운다"가 맞지만,
          // primary 는 이 배너가 유일한 동의 UI 라 그대로 두면 동의 수집이 통째로
          // 사라진다 — 조용히 죽는 대신 자체 지역 판정으로 한 번 더 받친다.
          // 표시하는 쪽(안전한 쪽)으로만 기울고, 이미 결정한 방문자는 needsConsent() 가 거른다.
          if (this.assumePrimary) return this.isEURegion() || this.isCCPARegion();

          return false;
      }
    }

    /**
     * Shopify 의 규제 판정. API 를 못 얻으면 안전한 쪽(표시)으로 기운다.
     */
    shopifyShouldShowBanner() {
      if (!this.privacy || typeof this.privacy.shouldShowBanner !== 'function') return true;

      try {
        return this.privacy.shouldShowBanner() === true;
      } catch (e) {
        return true;
      }
    }

    /**
     * getRegion() 결과의 국가 코드 부분 ("US-CA" → "US")
     */
    countryCode() {
      if (!this.region) return null;
      return String(this.region).slice(0, 2).toUpperCase();
    }

    /**
     * EU/EEA 지역인지 확인
     */
    isEURegion() {
      const country = this.countryCode();
      if (country) return EU_COUNTRIES.includes(country);

      // 지역을 모르면 Shopify 판정에 맡긴다. 단 primary 역할에서는 그 판정마저
      // 못 믿는 상황(자체 배너 OFF)이라, 동의를 놓치는 대신 표시하는 쪽으로 기운다.
      if (this.assumePrimary) return true;
      return this.shopifyShouldShowBanner();
    }

    /**
     * CCPA 등 "판매 옵트아웃" 대상 지역인지 확인
     */
    isCCPARegion() {
      if (this.saleOfDataRegion) return true;
      return String(this.region || '').toUpperCase() === 'US-CA';
    }

    /**
     * 현재 감지된 지역 정보 반환 (디버깅용)
     */
    getRegionInfo() {
      return {
        mode: this.regionMode,
        apiAvailable: this.privacy !== null,
        region: this.region,
        saleOfDataRegion: this.saleOfDataRegion,
        isEU: this.isEURegion(),
        isCCPA: this.isCCPARegion(),
        shouldShowBanner: this.shouldShowForRegion()
      };
    }

    /**
     * 동의 상태 리셋
     */
    reset() {
      try {
        localStorage.removeItem(this.cookieName);
      } catch (e) {
        // 무시
      }

      if (window.WokieeCookies) {
        WokieeCookies.remove(this.cookieName);
      }

      this.consent = this.loadConsent();
      this.notifyListeners();
    }

    /**
     * 동의 변경 리스너 등록
     */
    addListener(callback) {
      this.listeners.push(callback);
    }

    /**
     * 리스너 제거
     */
    removeListener(callback) {
      this.listeners = this.listeners.filter((l) => l !== callback);
    }

    /**
     * 모든 리스너에게 알림
     */
    notifyListeners() {
      this.listeners.forEach((callback) => {
        try {
          callback(this.consent);
        } catch (e) {
          // 리스너 하나가 죽어도 나머지는 돌린다
        }
      });

      window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
        detail: this.consent
      }));
    }

    /**
     * 현재 동의 상태 반환
     */
    getConsent() {
      return { ...this.consent };
    }
  }

  // 전역 인스턴스 생성
  window.CookieConsentManager = new CookieConsentManager();

  /**
   * Cookie Consent Custom Element
   */
  class CookieConsent extends HTMLElement {
    constructor() {
      super();
      this.manager = window.CookieConsentManager;
      this.preferencesVisible = false;
      this.shopifyBannerObserver = null;
    }

    connectedCallback() {
      this.role = this.dataset.bannerRole === BANNER_ROLE.PRIMARY ? BANNER_ROLE.PRIMARY : BANNER_ROLE.FALLBACK;
      this.manager.assumePrimary = this.role === BANNER_ROLE.PRIMARY;
      this.manager.setRegionMode(this.dataset.regionMode || REGION_MODE.AUTO);

      const duration = parseInt(this.dataset.cookieDuration, 10);
      if (!Number.isNaN(duration)) this.manager.cookieDuration = duration;

      this.bindEvents();
      this.watchShopifyBanner();

      // 테마 에디터에서는 머천트가 꾸밀 수 있도록 항상 표시
      if (window.Shopify && window.Shopify.designMode) {
        this.show();
        return;
      }

      this.manager.ready.then(() => this.decideVisibility());
    }

    disconnectedCallback() {
      this.stopWatchingShopifyBanner();
    }

    stopWatchingShopifyBanner() {
      if (this.shopifyBannerObserver) {
        this.shopifyBannerObserver.disconnect();
        this.shopifyBannerObserver = null;
      }
    }

    /**
     * 표시 여부 결정. 폴백 역할이면 Shopify 배너에 우선권을 준다.
     */
    decideVisibility() {
      if (this.role === BANNER_ROLE.FALLBACK && this.shopifyBannerWillShow()) return;
      if (this.manager.needsConsent()) this.show();
    }

    /**
     * Shopify 자체 쿠키 배너가 이 방문자에게 뜰 상황인지.
     *
     * ⚠️ window.privacyBanner 만 보면 안 된다 — storefront-banner.js 는 async 로 실리므로
     * 우리 판단 시점엔 아직 전역이 없을 수 있고, 그러면 커스텀 배너가 떴다가 Shopify 배너가
     * 뒤늦게 그려지며 사라지는 깜빡임이 생긴다. 스크립트 **태그의 존재**는 DOM 에 동기적으로
     * 있으므로, 어드민에서 Shopify 배너를 켰는지를 이걸로 먼저 판정한다.
     */
    shopifyBannerWillShow() {
      if (document.getElementById('shopify-pc__banner')) return true;

      const bannerScriptLoaded =
        (window.privacyBanner && typeof window.privacyBanner.loadBanner === 'function') ||
        document.querySelector('script[src*="storefront-banner"]') !== null;

      if (!bannerScriptLoaded) return false;
      return this.manager.shopifyShouldShowBanner();
    }

    /**
     * Shopify 배너가 뒤늦게 그려지면 커스텀 배너를 물린다 (이중 배너 방지).
     * 배너는 초기 로드 직후에만 뜨므로 10초 뒤 관찰을 끊는다.
     */
    watchShopifyBanner() {
      if (this.role !== BANNER_ROLE.FALLBACK || typeof MutationObserver === 'undefined') return;

      this.shopifyBannerObserver = new MutationObserver(() => {
        if (!document.getElementById('shopify-pc__banner')) return;
        this.hide();
        this.stopWatchingShopifyBanner();
      });

      this.shopifyBannerObserver.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => this.stopWatchingShopifyBanner(), 10000);
    }

    bindEvents() {
      this.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          this.handleAction(e.currentTarget.dataset.action);
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) {
          this.dismiss();
        }
      });
    }

    /**
     * 선택 없이 배너를 닫는 경로(X 버튼·Esc).
     *
     * 아직 동의를 한 번도 수집하지 못한 상태라면 닫아 주지 않는다 — 닫히는 순간
     * "선택하지 않고 넘어가기"가 거절보다 쉬운 길이 되어 다크패턴이 되기 때문이다.
     * 이미 결정을 남긴 방문자가 푸터 링크로 다시 연 경우에만 닫기가 열린다.
     */
    dismiss() {
      if (this.manager.needsConsent()) return;
      this.hide();
    }

    handleAction(action) {
      switch (action) {
        case 'accept':
          this.manager.acceptAll();
          this.hide();
          break;

        case 'reject':
          this.manager.rejectAll();
          this.hide();
          break;

        case 'toggle-preferences':
          this.togglePreferences();
          break;

        case 'save':
          this.savePreferences();
          this.hide();
          break;

        case 'close':
          // 동의 상태는 바꾸지 않는다. 미결정이면 dismiss() 가 닫기를 거부한다.
          this.dismiss();
          break;
      }
    }

    /**
     * 외부에서 "쿠키 설정"을 다시 열 때의 진입점 (cookie-preferences.js 가 호출)
     */
    openPreferences() {
      this.syncCheckboxes();
      this.show();

      // 환경설정 패널이 꺼져 있는 섹션 설정이면 배너만 다시 띄운다
      if (!this.querySelector('.cookie-consent__preferences')) return;
      if (!this.preferencesVisible) this.togglePreferences();
    }

    /**
     * 체크박스를 현재 동의 상태에 맞춘다
     */
    syncCheckboxes() {
      const categories = this.manager.getConsent().categories;

      this.querySelectorAll('.cookie-consent__preferences input[type="checkbox"]').forEach((checkbox) => {
        if (checkbox.disabled) return;
        if (Object.prototype.hasOwnProperty.call(categories, checkbox.name)) {
          checkbox.checked = categories[checkbox.name] === true;
        }
      });
    }

    togglePreferences() {
      const preferences = this.querySelector('.cookie-consent__preferences');
      const toggleBtn = this.querySelector('[data-action="toggle-preferences"]');
      const acceptBtn = this.querySelector('[data-action="accept"]');
      const rejectBtn = this.querySelector('[data-action="reject"]');
      const saveBtn = this.querySelector('[data-action="save"]');

      this.preferencesVisible = !this.preferencesVisible;

      if (preferences) {
        preferences.hidden = !this.preferencesVisible;
      }

      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', String(this.preferencesVisible));

      if (this.preferencesVisible) {
        if (toggleBtn) toggleBtn.hidden = true;
        if (acceptBtn) acceptBtn.hidden = true;
        if (rejectBtn) rejectBtn.hidden = true;
        if (saveBtn) saveBtn.hidden = false;
      } else {
        if (toggleBtn) toggleBtn.hidden = false;
        if (acceptBtn) acceptBtn.hidden = false;
        if (rejectBtn) rejectBtn.hidden = false;
        if (saveBtn) saveBtn.hidden = true;
      }
    }

    savePreferences() {
      const categories = {};

      this.querySelectorAll('.cookie-consent__preferences input[type="checkbox"]').forEach((checkbox) => {
        if (checkbox.disabled) return;
        categories[checkbox.name] = checkbox.checked;
      });

      this.manager.saveCustom(categories);
    }

    show() {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');

      // 미결정 상태에선 "선택 없이 닫기" 통로를 아예 노출하지 않는다 (dismiss() 주석 참조).
      // 눌러도 안 먹는 죽은 버튼을 남기지 않기 위해 표시 단계에서 감춘다.
      const closeBtn = this.querySelector('[data-action="close"]');
      if (closeBtn) closeBtn.hidden = this.manager.needsConsent();

      const firstButton = this.querySelector('button:not([hidden])');
      if (firstButton) {
        firstButton.focus();
      }
    }

    hide() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
    }
  }

  if (!customElements.get('cookie-consent')) {
    customElements.define('cookie-consent', CookieConsent);
  }

  /**
   * Helper: 동의 확인 후 쿠키 설정
   */
  window.setConsentedCookie = function (name, value, duration, unit, category = 'functional') {
    if (window.CookieConsentManager && window.CookieConsentManager.isAllowed(category)) {
      if (window.WokieeCookies) {
        WokieeCookies.set(name, value, duration, unit);
        return true;
      }
    }
    return false;
  };

  /**
   * Helper: 동의 확인 후 쿠키 읽기
   */
  window.getConsentedCookie = function (name, options = {}, category = 'functional') {
    if (window.CookieConsentManager && window.CookieConsentManager.isAllowed(category)) {
      if (window.WokieeCookies) {
        return WokieeCookies.get(name, options);
      }
    }
    return options.asArray ? [] : null;
  };

}
